import React, {useState, Fragment} from 'react'
import {isEmpty} from 'lodash'
import {useParams, useHistory} from 'react-router-dom'
import useBoolState from 'admin/hooks/useBoolState'
import useEffect from 'admin/hooks/useEffect'
import usePaymentRule from 'admin/hooks/usePaymentRule'
import useDocument from 'admin/hooks/useDocument'
import useStateObject from 'admin/hooks/useStateObject'
import InputField from 'admin/components/shared/InputField'
import Modal from 'admin/components/shared/Modal'
import AccountField from 'admin/components/shared/AccountField'
import PaymentRuleCondition from './PaymentRuleCondition'
import Deletion from '../Deletion'
import Header from '../shared/Header'
import useModal from '../../hooks/useModal'

const DEFAULT_CONDITION = {
  fieldName: 'any_field',
  matchType: 'contains'
}

export default () => {
  const history = useHistory()
  const {organisationID} = useParams()
  const {paymentRuleId, popModal} = useModal()
  const {paymentRule, errors, update, destroy} = usePaymentRule(organisationID, paymentRuleId)
  const [form, setForm, resetForm] = useStateObject(
    isEmpty(paymentRule) ? {conditions: [DEFAULT_CONDITION], description: 'Payment received'} : paymentRule
  )
  const [removedConditions, setRemovedConditions] = useState([])
  const [showDeleteModal, setShowDeleteModal, toggleShowDeleteModal] = useBoolState()

  useDocument({title: `Payment rule ${paymentRuleId}`})

  useEffect(() => setForm(paymentRule), [paymentRule], false)

  const handleUpdate = e => {
    e.preventDefault()
    let conditions = [...form.conditions]
    removedConditions.forEach(condition => conditions.push({...condition, _destroy: true}))
    update({...form, conditions})
  }

  const handleDelete = ({notes}) => {
    destroy({recordIds: [paymentRuleId], notes}, handleClose)
  }

  const handleConditionChange = (index, change) => {
    let conditions = [...form.conditions]
    conditions[index] = {...conditions[index], ...change}
    setForm({conditions})
  }

  const handleAddCondition = () => {
    let conditions = [...form.conditions]
    conditions.push(DEFAULT_CONDITION)
    setForm({conditions})
  }

  const handleRemoveCondition = index => {
    let conditions = [...form.conditions]
    let removed = conditions.splice(index, 1)
    setRemovedConditions([...removedConditions, ...removed])
    if (isEmpty(conditions)) conditions = [DEFAULT_CONDITION]
    setForm({conditions})
  }

  const handleClose = e => {
    e?.preventDefault()
    resetForm(paymentRule)
    popModal('payment-rule', ['paymentRuleId'])
  }

  return (
    <Fragment>
      <Modal onClose={handleClose} flashId='edit-payment-rule'>
        <Header title='Edit payment rule'>
          <button className='button is-danger is-outlined' onClick={toggleShowDeleteModal}>
            Delete
          </button>
          <button className='button is-success' onClick={handleUpdate}>
            Save
          </button>
        </Header>
        <section className='section'>
          <div className='container'>
            <p className='title is-5 mt-0'>When a payment's...</p>

            {form.conditions.map((condition, i) => (
              <PaymentRuleCondition
                key={i}
                index={i}
                isAlone={form.conditions.length === 1}
                {...condition}
                errors={errors.conditions && errors.conditions[i]}
                onChange={handleConditionChange}
                onRemove={handleRemoveCondition}
              />
            ))}

            <div className='buttons is-right'>
              <button className='button is-outlined is-dark' onClick={handleAddCondition}>
                Add another condition
              </button>
            </div>

            <p className='title is-5 mt-0'>match it to the account...</p>

            <AccountField
              placeholder='Account'
              value={form.account?.displayName}
              organisationID={organisationID}
              onSelect={account => setForm({account, accountId: account?.id})}
              errors={errors.account}
            />

            <p className='title is-5'>and set the payment description to...</p>

            <InputField
              placeholder='Description'
              showLabel={false}
              name='description'
              value={form.description || ''}
              errors={errors.description}
              onChange={setForm}
            />
          </div>
        </section>
      </Modal>

      {showDeleteModal && <Deletion onClose={toggleShowDeleteModal} onConfirm={handleDelete} />}
    </Fragment>
  )
}
